import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fetchGeolocation, submitLead, getCountryDialCodes, preparLeadData } from '../services/api';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    subject: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const countryCodes = getCountryDialCodes();

  useEffect(() => {
    const loadGeolocation = async () => {
      const data = await fetchGeolocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const matchingCountry = countryCodes.find(c => c.countryCode === data.data.countryCode);
        if (matchingCountry) {
          setFormData(prev => ({ ...prev, countryCode: matchingCountry.code }));
        }
      }
    };
    
    loadGeolocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);
      
      // Prepare form data with subject in message
      const formDataWithSubject = {
        ...formData,
        message: `${formData.subject ? formData.subject + ': ' : ''}${formData.message}`
      };
      
      const leadData = preparLeadData(formDataWithSubject, geoData, selectedCountry);

      console.log('Submitting lead data:', leadData);
      
      const result = await submitLead(leadData);
      
      console.log('API Result:', result);

      if (result.success && result.statusCode === 200 && result.customerId) {
        setSuccess(true);
        setFormData({ name: '', email: '', countryCode: '+1', phone: '', subject: '', message: '' });
        setTimeout(() => {
          // Full page redirect (external URL)
          window.location.href = `/application/main?customerid=${result.customerId}`;
        }, 2000);
      } else {
        // Show the actual API error message
        const errorMsg = result.message || result.data?.message || 'An error occurred. Please try again.';
        console.error('Form submission failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Get in <span className="text-yellow-400">Touch</span>
            </h1>
            <p className="text-xl text-white/90">
              Have questions? We're here to help. Contact our trademark experts today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Reach out to us through any of these channels. We're always ready to assist you.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-full flex-shrink-0">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <div className="text-gray-600">+1 (844) 205 0123</div>
                    <div className="text-sm text-gray-500">Mon-Fri 9am-6pm EST</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-yellow-100 p-3 rounded-full flex-shrink-0">
                    <Mail className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <div className="text-gray-600">info@mytmbrand.com</div>
                    <div className="text-sm text-gray-500">24/7 support</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-full flex-shrink-0">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Location</div>
                    <div className="text-gray-600">United States</div>
                    <div className="text-sm text-gray-500">Serving all 50 states</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-yellow-100 p-3 rounded-full flex-shrink-0">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Business Hours</div>
                    <div className="text-gray-600">Monday - Friday</div>
                    <div className="text-sm text-gray-500">9:00 AM - 6:00 PM EST</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-xl p-6">
                <h3 className="text-xl font-bold mb-2">Quick Response</h3>
                <p className="text-white/90 text-sm">
                  Most inquiries receive a response within 2 hours during business hours. For urgent matters, please call us directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}
                
                {success && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Contact Number *</Label>
                    <div className="flex space-x-2 mt-2">
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) => setFormData({ ...formData, countryCode: value })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code} {item.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        placeholder="1234567890"
                        maxLength={10}
                        pattern="[0-9]{10}"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="What is your inquiry about?"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your needs..."
                      rows={6}
                      className="mt-2"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked <span className="text-red-600">Questions</span>
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: 'How long does the trademark registration process take?',
                  a: 'The entire USPTO process typically takes 8-12 months from filing to registration. However, we file your application within 24-48 hours of receiving your information.'
                },
                {
                  q: 'What are your business hours?',
                  a: 'Our office is open Monday through Friday, 9:00 AM to 6:00 PM EST. However, our email support is monitored 24/7, and we respond to all inquiries promptly.'
                },
                {
                  q: 'Do you offer free consultations?',
                  a: 'Yes! We offer free initial consultations to discuss your trademark needs and recommend the best service package for your situation.'
                },
                {
                  q: 'Can you help with international trademarks?',
                  a: 'While we specialize in U.S. trademark registration, we can guide you on international trademark protection strategies and connect you with our global partners if needed.'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};